import { AppModal } from '../../components/modal';
import { ShippingAddress } from '../../components/shipping-address';
import { useGeolocation } from '../../context/geolocation-context';
import { formatPhoneNumber } from '../../helper/utils';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { iDeliveryLocation } from '../../types';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import DeliveryLocation from '../../components/delivery-location';
import ShippingService from '../../services/shipping-service';
import { useAuth, User } from '../../context/auth-context';
import { Link } from 'react-router-dom';
import { Trash3Fill } from 'react-bootstrap-icons';

const UserShippingAddress = () => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowModal, setIsShowModal] = useState<boolean>(false);
    const [editSavedLocation, setEditSavedLocation] = useState<iDeliveryLocation>({});
    const [shippingAddresses, setShippingAddresses] = useState<iDeliveryLocation[]>([]);
    const [selectedDeliveryLocation, setSelectedDeliveryLocation] = useState<iDeliveryLocation|undefined>(undefined);
    const { handleFetchLocationFromAddress } = useGeolocation();
    const { user } = useAuth();
    const [errors, setErrors] = useState<string>("");

    const handleModal = (toggle: boolean) => setIsShowModal(toggle);

    const isEdittingShippingAddress = () => Object.keys(editSavedLocation).length > 0;

    const createShippingLocation = async (deliveryLocation: iDeliveryLocation, user: User) => {

        let newDeliveryLocation: iDeliveryLocation = {
            latitude: 0,
            longitude: 0,
            ...deliveryLocation,
            phone: formatPhoneNumber(deliveryLocation.phone||""),
            userId: user.id,
        }

        const updatedDeliveryLocation = await handleFetchLocationFromAddress(newDeliveryLocation);
    
        if (updatedDeliveryLocation) {
            // Send the server a request to update active address.
            const { status, message } = await ShippingService.createShippingAddress(updatedDeliveryLocation);

            if (status !== 201) {
                setErrors(message);
                return  ;
            }

            void fetchSavedLocationAddress(user);
        }
    }
    
    const updateShippingLocation = async (edittedDeliveryLocation: iDeliveryLocation, user: User) => {

        let updatedDeliveryLocation = edittedDeliveryLocation;

        if (updatedDeliveryLocation.latitude === 0 || updatedDeliveryLocation.longitude === 0) {
            const locationAddress = await handleFetchLocationFromAddress(updatedDeliveryLocation);

            if (locationAddress) {
                updatedDeliveryLocation = locationAddress;
            }
        }

    
        if (updatedDeliveryLocation) {

            // Send the server a request to update active address.
            const { status, message } = await ShippingService.updateShippingAddress(updatedDeliveryLocation);

            if (status !== 200) {
                setErrors(message);
                return  ;
            }

            void fetchSavedLocationAddress(user);
        }
    }


    const handleLocationSetter = useCallback((deliveryLocation: iDeliveryLocation) => {

        if (!user?.id) {
            return ;
        }

        isEdittingShippingAddress() 
            ? void updateShippingLocation(deliveryLocation, user) 
            : void createShippingLocation(deliveryLocation, user);

        setTimeout(() => {
            handleModal(false);
        }, 10); 

    }, [handleModal, formatPhoneNumber, user?.id]);


    const fetchSavedLocationAddress = async (user: User) => {
        try {

            setIsLoading(true);
            const { status, data, message } = await ShippingService.getShippingAddress(user.id);

            if (status !== 200) {
                setErrors(message);
                return ;
            }

            const newShippingLocationBook: iDeliveryLocation[] = (data as iDeliveryLocation[]);

            setShippingAddresses(newShippingLocationBook);

            setSelectedDeliveryLocation(newShippingLocationBook.find((shipAddr) => shipAddr.defaults));
            
        } catch (error) {
            setErrors((error as Error).message);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {

        if (!user?.id) {
            return ;
        }

        void fetchSavedLocationAddress(user);

    }, [user?.id]);

    const handleUpdateLocation = async (newUserLocation: iDeliveryLocation) => await ShippingService.updateShippingAddress(newUserLocation);

    const handleLocationActiveChange = useCallback((location: iDeliveryLocation) => {
        
        // Send the server a request to update active address.
        const newUserLocation = {...location, defaults: true};
        void handleUpdateLocation(newUserLocation);
        setSelectedDeliveryLocation(newUserLocation);
    }, [])

    const handleAddressLocationChange = (location: iDeliveryLocation) => {
        setEditSavedLocation(location);
        handleModal(true);
    }

    const handleDeleteLocationAddress = useCallback(async (newUserLocation: iDeliveryLocation) => {

        if (!newUserLocation?.id || !user?.id) {
            return ;
        }

        const { status, message } = await ShippingService.deleteShippingAddress(newUserLocation.id);

        if (status !== 200) {
            setErrors(message);
            return ;
        }

        void fetchSavedLocationAddress(user);
    }, [user?.id])

    return (
        <Container>
            <Row className='mb-4'>
                <Col xs={12} sm={7}><h4 style={{ fontSize: '1.4rem' }}>Address</h4></Col>
                <Col xs={12} sm={5}>
                    <Button 
                        variant='outline-success' 
                        size='sm' 
                        className='rounded' 
                        type='button'
                        onClick={() => {
                            setEditSavedLocation({});
                            handleModal(true)
                        }}
                    >Add a new address</Button>
                </Col>
            </Row>
            {errors && (<Alert className='py-2' style={{fontSize: '.9rem'}} variant='danger'>{errors}</Alert>)}
            {isLoading ? (<Spinner animation='border' size='sm' />) : (
                <Row>
                    {
                        shippingAddresses.length === 0 ? (
                            <h5 style={{ fontSize: '.9rem', color: '#868686' }}>No address setup.</h5>
                        )  : 
                        (shippingAddresses.map((location: iDeliveryLocation, i:number) => (
                            <Col xs={12} md={4} key={`shipping-address-${i}`} className='mb-3'>     
                                <Card>
                                    <Card.Body style={{ fontSize: '.9rem', color: '#868686' }}>
                                        <Row>
                                            <Col xs={10}>
                                                <Form.Check
                                                    inline
                                                    label={ location.heading }
                                                    name="group"
                                                    type="radio"
                                                    checked={selectedDeliveryLocation?.id === location.id}
                                                    id={`shipping-address-inline-radio-${i}`}
                                                    onChange={() => handleLocationActiveChange(location)}
                                                />
                                            </Col>
                                            <Col xs={2} className='text-right d-flex align-items-center'>
                                                <PencilSquareIcon 
                                                    width={20} 
                                                    height={20} 
                                                    className='text-muted' 
                                                    style={{cursor: 'pointer'}} 
                                                    onClick={() => handleAddressLocationChange(location)}
                                                />
                                            </Col>
                                            <Col xs={12}><DeliveryLocation location={location} /></Col>
                                            <Col xs={12}>
                                                <Link 
                                                    style={{color: '#f2333b', verticalAlign: 'baseline'}} 
                                                    to='#' 
                                                    onClick={() => handleDeleteLocationAddress(location)} 
                                                    className='px-3 m-0'>
                                                    <Trash3Fill width={15} height={15} color='#f2333b' />
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )))
                    }
                </Row>
            )}
            <AppModal 
                isShowModal={isShowModal}
                handleCloseModal={handleModal}
                title={
                    <div className='p-0 m-0 mt-2'>
                        <h3 className='m-0 p-0' style={{ fontSize: '1rem' }}>
                            { isEdittingShippingAddress() ? 'Editing Shipping Address' : 'New Shipping Address'} 
                        </h3>
                        <p className='m-0 p-0' style={{
                            color: '#868686',
                            fontSize: '.8rem'
                        }}>{ isEdittingShippingAddress() ? 'Edit' : 'Add new' } shipping address for your order delivery.</p>
                    </div>
                }
                size=''
                children={
                    <ShippingAddress 
                        payload={editSavedLocation} 
                        handleModal={handleModal} 
                        handleLocationSetter={handleLocationSetter} 
                    />
                }
            />
        </Container>
    )
}

export default UserShippingAddress
